<script setup>
// FLOW-03a (Task 7.1, depois 7.3; SPEC S2, S7 e Gate 1 Q2, Q5, Q6) — os gatilhos de um flow, embaixo
// da linha dele na lista de Flows (`FlowsDaConta.vue`), no lugar do aviso de que webhook e agenda "não
// aparecem aqui". Um por linha: o selo com o tipo e a regra ("Agenda · a cada 5 min", "Webhook ·
// POST"), que abre o detalhe; o liga e pausa; a próxima batida da agenda; e as contagens de hoje
// (disparos, recusas e puladas), com a recusa em destaque, porque é o que pede atenção.
//
// Só mostra: recebe os gatilhos do flow como o `GET /powerflow/gatilhos` os dá (Task 5.4) e devolve
// `alternar` e `abrir`. Quem liga e pausa é quem vê a aba (Q5), então o botão aparece sempre que
// `podeOperar`.
//
// O gatilho que acabou de nascer com a publicação vem pausado (Q2): em vez de só "Pausado", ele diz
// "Pausado · ligue para começar" enquanto nunca foi ligado (`ligado_em` nulo, resposta 2 de UX).
//
// **A agenda** não tem contagem de disparos do dia (o PowerTools guarda só a última batida e as
// puladas, P17): no lugar, "última: hoje 14:30" (ou "ainda não bateu"), e as puladas de hoje quando
// há. Nunca "Nada hoje", que diria que ela não bateu (L9d, O4).
//
// FLOW-03b (Task 3.8; PLAN P14; mockup `gatilho-unico.html`, tela 7): um gatilho por flow, numa linha
// só, com o ícone na cor do tipo (`tiposDeGatilho.js`). O flow publicado sem agenda nem webhook é de
// Agente, e ganha a linha dele no lugar da dica de antes: o agente o chama como tool, e não há o que
// ligar nem contar. O id do gatilho sai da linha: o tipo já o diz, e o do gatilho único é o tipo.
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { TIPOS_DE_GATILHO } from '../settings/powerTools/flow/tiposDeGatilho';
import { FAMILIAS } from '../settings/powerTools/flow/tiposDePasso';
import {
  ICONES_DOS_GATILHOS,
  contagensPorGrupo,
  nuncaLigado,
  useGatilhoNaTela,
} from './gatilhosNaTela';

defineProps({
  // O gatilho da versão publicada, `{id, gatilho_id, tipo, config, ligado, ligado_em,
  // contagens_do_dia, estado_da_agenda?}`, ou nulo: o flow de Agente.
  gatilho: { type: Object, default: null },
  podeOperar: { type: Boolean, default: true },
});

const emit = defineEmits(['alternar', 'abrir']);

const { t } = useI18n();
const { rotuloDoTipo, resumoDaRegra, quandoCurto, contagem } =
  useGatilhoNaTela();

const corDo = tipo => FAMILIAS[TIPOS_DE_GATILHO[tipo].familia].texto;

const proximaDe = gatilho =>
  gatilho.tipo === 'agenda' && gatilho.ligado
    ? gatilho.estado_da_agenda?.proxima_em
    : null;

const fusoDe = gatilho => gatilho.config?.fuso || 'America/Sao_Paulo';

const numerosDe = gatilho => {
  const grupos = contagensPorGrupo(gatilho.contagens_do_dia);
  if (gatilho.tipo === 'agenda') {
    grupos.pulada = Math.max(
      grupos.pulada,
      gatilho.estado_da_agenda?.puladas_hoje || 0
    );
  }
  return grupos;
};
const ehAgenda = gatilho => gatilho.tipo === 'agenda';
const ultimaDe = gatilho =>
  ehAgenda(gatilho) ? gatilho.estado_da_agenda?.ultima_prevista : null;
// Só no webhook: na agenda, a contagem do dia não existe, e "Nada hoje" mentiria.
const semNadaHoje = gatilho => {
  if (ehAgenda(gatilho)) return false;
  const { disparo, recusa, pulada } = numerosDe(gatilho);
  return !disparo && !recusa && !pulada;
};
</script>

<template>
  <div class="flex flex-col min-w-0" data-gatilhos-da-linha>
    <div
      class="flex flex-wrap items-center gap-x-4 gap-y-1.5 py-2 ps-2 pe-2 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-alpha-1"
      :data-tipo="gatilho?.tipo || 'agente'"
      :data-gatilho="gatilho?.gatilho_id"
    >
      <template v-if="!gatilho">
        <span
          class="inline-flex items-center gap-1.5 h-7 px-2 text-xs font-medium text-n-slate-12"
        >
          <span
            class="size-3.5 shrink-0"
            :class="[ICONES_DOS_GATILHOS.agente, corDo('agente')]"
            aria-hidden="true"
          />
          {{ rotuloDoTipo('agente') }}
        </span>
        <span class="text-xs text-n-slate-11" data-agente>
          {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.NONE') }}
        </span>
      </template>

      <template v-else>
        <button
          type="button"
          class="inline-flex items-center min-w-0 gap-1.5 h-7 px-2 rounded-md text-xs text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-1 focus-visible:outline-n-brand"
          :aria-label="
            t('POWER_TOOLS.POWERFLOW.TRIGGERS.OPEN', {
              gatilho: gatilho.gatilho_id,
            })
          "
          data-abrir-gatilho
          @click="emit('abrir', gatilho)"
        >
          <span
            class="size-3.5 shrink-0"
            :class="[ICONES_DOS_GATILHOS[gatilho.tipo], corDo(gatilho.tipo)]"
            aria-hidden="true"
          />
          <span class="font-medium truncate">
            {{
              t('POWER_TOOLS.POWERFLOW.TRIGGERS.CHIP', {
                tipo: rotuloDoTipo(gatilho.tipo),
                resumo: resumoDaRegra(gatilho),
              })
            }}
          </span>
        </button>

        <label
          class="inline-flex items-center gap-2 text-xs text-n-slate-12"
          :title="
            nuncaLigado(gatilho)
              ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.FIRST_TIME_HINT')
              : undefined
          "
        >
          <Switch
            v-if="podeOperar"
            :model-value="gatilho.ligado"
            :aria-label="
              gatilho.ligado
                ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.TURN_OFF', {
                    gatilho: gatilho.gatilho_id,
                  })
                : t('POWER_TOOLS.POWERFLOW.TRIGGERS.TURN_ON', {
                    gatilho: gatilho.gatilho_id,
                  })
            "
            data-alternar
            @update:model-value="ligado => emit('alternar', gatilho, ligado)"
          />
          <span
            v-if="gatilho.ligado"
            class="whitespace-nowrap"
            data-estado-do-gatilho
          >
            {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.ON') }}
          </span>
          <span
            v-else-if="nuncaLigado(gatilho)"
            class="inline-flex items-center gap-1 whitespace-nowrap"
            data-estado-do-gatilho
          >
            <span
              class="rounded-full size-1.5 bg-n-amber-9"
              aria-hidden="true"
            />
            {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.FIRST_TIME') }}
          </span>
          <span
            v-else
            class="whitespace-nowrap text-n-slate-11"
            data-estado-do-gatilho
          >
            {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.OFF') }}
          </span>
        </label>

        <span
          v-if="proximaDe(gatilho)"
          class="text-xs whitespace-nowrap tabular-nums text-n-slate-11"
          data-proxima
        >
          {{
            t('POWER_TOOLS.POWERFLOW.TRIGGERS.NEXT', {
              quando: quandoCurto(proximaDe(gatilho), fusoDe(gatilho)),
            })
          }}
        </span>

        <span
          class="flex flex-wrap items-center gap-x-1.5 ms-auto text-xs tabular-nums text-n-slate-11"
          data-contagens
        >
          <span class="sr-only">
            {{
              ehAgenda(gatilho)
                ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.DETAIL.RUNS')
                : t('POWER_TOOLS.POWERFLOW.TRIGGERS.COUNTS.LABEL')
            }}
          </span>
          <template v-if="semNadaHoje(gatilho)">
            {{ t('POWER_TOOLS.POWERFLOW.TRIGGERS.COUNTS.NONE') }}
          </template>
          <template v-else>
            <span v-if="ehAgenda(gatilho)" data-ultima>
              {{
                ultimaDe(gatilho)
                  ? t('POWER_TOOLS.POWERFLOW.TRIGGERS.LAST', {
                      quando: quandoCurto(ultimaDe(gatilho), fusoDe(gatilho)),
                    })
                  : t('POWER_TOOLS.POWERFLOW.TRIGGERS.COUNTS.NO_RUN_YET')
              }}
            </span>
            <span v-else class="text-n-slate-12">
              {{ contagem('FIRED', numerosDe(gatilho).disparo) }}
            </span>
            <template v-if="numerosDe(gatilho).recusa">
              <span
                class="rounded-full size-[3px] bg-n-slate-10"
                aria-hidden="true"
              />
              <span
                class="inline-flex items-center gap-1 font-medium text-n-ruby-11"
                data-recusas
              >
                <span
                  class="i-lucide-shield-alert size-3.5"
                  aria-hidden="true"
                />
                {{ contagem('REFUSED', numerosDe(gatilho).recusa) }}
              </span>
            </template>
            <template v-if="numerosDe(gatilho).pulada">
              <span
                class="rounded-full size-[3px] bg-n-slate-10"
                aria-hidden="true"
              />
              <span data-puladas>
                {{
                  contagem(
                    ehAgenda(gatilho) ? 'SKIPPED_TODAY' : 'SKIPPED',
                    numerosDe(gatilho).pulada
                  )
                }}
              </span>
            </template>
          </template>
        </span>
      </template>
    </div>
  </div>
</template>
